import { AudioPlayer, AudioRecorder } from '@alter/api-client';
import type { MiSimulacionResponse } from '@alter/types';
import { useQueryClient } from '@tanstack/react-query';
import { Volume2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../api/bootstrap';
import { AvatarStage } from '../../avatar/AvatarStage';
import { MarcadorPaciente } from '../../avatar/MarcadorPaciente';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorMessage } from '../../components/ErrorMessage';
import { PiePagina } from '../../components/PiePagina';
import { Spinner } from '../../components/Spinner';
import { esSinSimulacion, formatError } from '../../lib/formatError';
import { useAuthStore } from '../../stores/authStore';
import { useSimulacionStore } from '../../stores/simulacionStore';
import { MI_SIMULACION_QUERY_KEY, useMiSimulacion } from '../mi-simulacion/useMiSimulacion';
import { BotonPtt } from './BotonPtt';
import { Cajonera } from './Cajonera';
import { DiagnosticoPanel } from './DiagnosticoPanel';
import { EncabezadoSesion } from './EncabezadoSesion';
import { OrquestadorSesion, type ControlAvatar } from './orquestador';
import { PanelTranscripcion } from './PanelTranscripcion';
import { guardarPreferencias, leerPreferencias, type PreferenciasSesion } from './preferencias';
import { sesionActiva, type EstadoSesion } from './sesionMachine';
import { TEXTOS, textoAviso, textoErrorSesion, textoFalla } from './textos';

/**
 * Pantalla de la entrevista (`/simulacion/:id`), tipo videollamada
 * (especificación §4.3–§4.9; D-14): el avatar es el elemento principal, el
 * PTT va abajo y destacado, la transcripción a un costado (abajo en
 * pantallas angostas). La lógica vive en `sesionMachine` (estado) y
 * `orquestador` (efectos); este componente dibuja y delega los clics.
 */
export function SesionPage() {
  const { data, isLoading, error, refetch, isRefetching } = useMiSimulacion();

  if (isLoading) {
    return (
      <Marco>
        <div className="flex flex-1 items-center justify-center">
          <Spinner size={28} />
        </div>
      </Marco>
    );
  }
  if (error || !data) {
    return (
      <Marco>
        <Centrado>
          {esSinSimulacion(error) ? (
            <p className="text-sm text-slate-700">{formatError(error, 'mi_simulacion')}</p>
          ) : (
            <ErrorMessage>{formatError(error, 'mi_simulacion')}</ErrorMessage>
          )}
          <Button variant="secondary" onClick={() => void refetch()} disabled={isRefetching}>
            {isRefetching ? <Spinner /> : 'Actualizar'}
          </Button>
        </Centrado>
      </Marco>
    );
  }
  return <Sesion miSimulacion={data} />;
}

function Sesion({ miSimulacion }: { miSimulacion: MiSimulacionResponse }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sesion = useSimulacionStore((s) => s.sesion);
  const usuarioNombre = useAuthStore((s) => s.usuarioNombre);
  const orqRef = useRef<OrquestadorSesion | null>(null);
  const avatarRef = useRef<ControlAvatar | null>(null);
  const [cajonera, setCajonera] = useState(false);
  const [confirmandoSalida, setConfirmandoSalida] = useState(false);
  const [preferencias, setPreferencias] = useState<PreferenciasSesion>(() =>
    leerPreferencias(almacenamiento()),
  );

  useEffect(() => {
    useSimulacionStore.getState().reiniciar(miSimulacion.reconexion ? 'retomar' : 'nuevo');
    const api = getApi();
    const orq = new OrquestadorSesion({
      ws: api.wsClient,
      api: api.simulacionClient,
      obtenerToken: () => api.tokenStorage.get(),
      obtenerMicrofono: () => navigator.mediaDevices.getUserMedia({ audio: true }),
      crearGrabador: (stream) => new AudioRecorder({ stream }),
      reproductor: new AudioPlayer(),
      crearContextoAudio: () => new AudioContext(),
      dispatch: (e) => useSimulacionStore.getState().dispatch(e),
      getEstado: () => useSimulacionStore.getState().sesion,
      registrar: (tipo, nombre, datos) =>
        useSimulacionStore.getState().registrar(tipo, nombre, datos),
    });
    orq.conectarAvatar(avatarRef.current);
    orqRef.current = orq;
    return () => {
      orq.destruir();
      orqRef.current = null;
      // Si la sesión arrancó, lo guardado de mi-simulacion quedó viejo
      // (`reconexion: false` con una entrevista en curso): al volver a entrar
      // se pide de nuevo, o la pantalla ofrecería "Iniciar" y pediría otro
      // ticket. En `preparada` no se toca: es el desmontaje simulado de
      // StrictMode y no debe disparar un segundo GET.
      if (useSimulacionStore.getState().sesion.fase !== 'preparada') {
        queryClient.removeQueries({ queryKey: MI_SIMULACION_QUERY_KEY });
      }
    };
  }, [miSimulacion, queryClient]);

  const onAvatarListo = useCallback((control: ControlAvatar | null) => {
    avatarRef.current = control;
    orqRef.current?.conectarAvatar(control);
  }, []);

  const cambiarPreferencias = (p: PreferenciasSesion) => {
    setPreferencias(p);
    guardarPreferencias(almacenamiento(), p);
  };

  /** Volver al Paso 2 con datos frescos: el GET se vuelve a pedir al entrar. */
  const volverAlInicio = () => {
    queryClient.removeQueries({ queryKey: MI_SIMULACION_QUERY_KEY });
    navigate('/mi-simulacion');
  };

  const salir = async () => {
    await getApi().authClient.logout();
    queryClient.removeQueries({ queryKey: MI_SIMULACION_QUERY_KEY });
    useAuthStore.getState().reset();
    navigate('/login', { replace: true });
  };

  // Salir a mitad de la entrevista no la cierra: queda retomable.
  const pedirSalida = () => (sesionActiva(sesion) ? setConfirmandoSalida(true) : void salir());

  const orq = () => orqRef.current;
  const acciones: Acciones = {
    iniciar: () => orq()?.iniciar(miSimulacion),
    reintentarConexion: () => void orq()?.reintentarConexion(),
    reiniciar: () => orq()?.reiniciar('nuevo'),
    reintentarCierre: () => void orq()?.reintentarCierre(),
    volverAlInicio,
    volverAIngresar: () => void salir(),
  };

  if (sesion.fase === 'no_compatible') {
    return (
      <Marco encabezado={<EncabezadoSesion {...propsEncabezado()} />}>
        <Centrado>
          <p className="text-base font-medium text-slate-900">{TEXTOS.noCompatible}</p>
        </Centrado>
      </Marco>
    );
  }

  function propsEncabezado() {
    return {
      usuarioNombre,
      cajoneraAbierta: cajonera,
      onAlternarCajonera: () => setCajonera((v) => !v),
      onSalir: pedirSalida,
    };
  }

  const tarjeta = tarjetaDeFase(sesion, miSimulacion, acciones);
  const mostrarControles = sesion.fase === 'en_curso' || sesion.fase === 'reconectando';

  return (
    <Marco encabezado={<EncabezadoSesion {...propsEncabezado()} />}>
      {cajonera && <Cajonera preferencias={preferencias} onCambiar={cambiarPreferencias} />}

      <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 lg:flex-row lg:overflow-hidden">
        <section className="flex min-h-0 flex-1 flex-col gap-4">
          {/* El "video" del paciente. */}
          <div className="relative min-h-[40vh] flex-1 overflow-hidden rounded-2xl bg-slate-800">
            {preferencias.mostrarAvatar ? (
              <AvatarStage avatar={miSimulacion.escenario.avatar} onListo={onAvatarListo} />
            ) : (
              <MarcadorPaciente />
            )}
            {sesion.fase === 'reconectando' && (
              <span className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
                <Spinner size={12} /> {TEXTOS.reconectando}
              </span>
            )}
            {tarjeta && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900/40 p-4">
                <div className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 text-center shadow-lg">
                  {tarjeta}
                </div>
              </div>
            )}
          </div>

          {sesion.aviso && (
            <p
              role="status"
              className="rounded-lg bg-amber-100 px-3 py-2 text-center text-sm text-amber-900"
            >
              {textoAviso(sesion.aviso.codigo)}
            </p>
          )}

          {mostrarControles && (
            <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
              <div>
                {sesion.reproduccionBloqueada && (
                  <Button variant="secondary" onClick={() => orq()?.reproducirPendiente()}>
                    <Volume2 size={16} /> Reproducir
                  </Button>
                )}
              </div>
              <BotonPtt sesion={sesion} onPulsar={() => orq()?.pttPulsado()} />
              <div className="flex justify-end">
                <Button variant="secondary" onClick={() => orq()?.solicitarCierre()}>
                  Finalizar entrevista
                </Button>
              </div>
            </div>
          )}
        </section>

        {(preferencias.mostrarTranscripcion || import.meta.env.DEV) && (
          <aside className="flex max-h-[45vh] min-h-0 flex-col gap-4 lg:max-h-none lg:w-96">
            {preferencias.mostrarTranscripcion && (
              <PanelTranscripcion lineas={sesion.transcripcion} />
            )}
            {import.meta.env.DEV && (
              <details className="flex min-h-0 flex-col">
                <summary className="cursor-pointer text-xs text-amber-800">
                  Diagnóstico (solo desarrollo)
                </summary>
                <div className="mt-2 max-h-80 overflow-hidden">
                  <DiagnosticoPanel />
                </div>
              </details>
            )}
          </aside>
        )}
      </main>

      {sesion.confirmandoCierre && (
        <ConfirmDialog
          titulo={TEXTOS.confirmarCierreTitulo}
          confirmar="Finalizar"
          onConfirmar={() => void orq()?.confirmarCierre()}
          onCancelar={() => orq()?.cancelarCierre()}
        >
          {TEXTOS.confirmarCierreDetalle}
        </ConfirmDialog>
      )}
      {confirmandoSalida && (
        <ConfirmDialog
          titulo={TEXTOS.confirmarSalidaTitulo}
          confirmar="Salir"
          onConfirmar={() => void salir()}
          onCancelar={() => setConfirmandoSalida(false)}
        >
          {TEXTOS.confirmarSalidaDetalle}
        </ConfirmDialog>
      )}
    </Marco>
  );
}

interface Acciones {
  iniciar: () => void;
  reintentarConexion: () => void;
  reiniciar: () => void;
  reintentarCierre: () => void;
  volverAlInicio: () => void;
  volverAIngresar: () => void;
}

/** Tarjeta sobre el "video" para cada fase que no es la conversación en sí. */
function tarjetaDeFase(s: EstadoSesion, ms: MiSimulacionResponse, a: Acciones): ReactNode | null {
  switch (s.fase) {
    case 'preparada':
      return (
        <>
          <h1 className="text-lg font-semibold text-slate-900">{ms.escenario.nombre}</h1>
          <p className="text-sm text-slate-600">
            {s.modo === 'retomar'
              ? 'Hay una entrevista en curso.'
              : 'Al iniciar, el navegador pide permiso para usar el micrófono.'}
          </p>
          <Button onClick={a.iniciar} className="w-full">
            {s.modo === 'retomar' ? 'Retomar entrevista' : 'Iniciar entrevista'}
          </Button>
        </>
      );
    case 'iniciando':
      return <Cargando texto="Conectando…" />;
    case 'inicio_fallido':
      return (
        <>
          <ErrorMessage>{textoFalla(s.errorCodigo)}</ErrorMessage>
          {s.errorCodigo === 'token' ? (
            <Button onClick={a.volverAIngresar} className="w-full">
              Volver a ingresar
            </Button>
          ) : (
            <Button onClick={a.iniciar} className="w-full">
              Reintentar
            </Button>
          )}
        </>
      );
    case 'reconexion_fallida':
      return (
        <>
          <p className="text-sm text-slate-700">{TEXTOS.reconexionFallida}</p>
          <Button onClick={a.reintentarConexion} className="w-full">
            Reintentar
          </Button>
        </>
      );
    case 'error_sesion': {
      const { texto, accion } = textoErrorSesion(s.errorCodigo);
      return (
        <>
          <p className="text-sm text-slate-700">{texto}</p>
          {accion === 'reintentar' && (
            <Button onClick={a.reiniciar} className="w-full">
              Reintentar
            </Button>
          )}
          {accion === 'volver_al_inicio' && (
            <Button onClick={a.volverAlInicio} className="w-full">
              Volver al inicio
            </Button>
          )}
          {accion === 'volver_a_ingresar' && (
            <Button onClick={a.volverAIngresar} className="w-full">
              Volver a ingresar
            </Button>
          )}
        </>
      );
    }
    case 'finalizando':
      return <Cargando texto="Finalizando…" />;
    case 'cierre_fallido':
      return (
        <>
          <ErrorMessage>{TEXTOS.cierreFallido}</ErrorMessage>
          <Button onClick={a.reintentarCierre} className="w-full">
            Reintentar
          </Button>
        </>
      );
    case 'finalizada':
      return (
        <>
          <h1 className="text-lg font-semibold text-slate-900">{TEXTOS.finalizada}</h1>
          <Button onClick={a.volverAlInicio} className="w-full">
            Volver al inicio
          </Button>
        </>
      );
    default:
      return null;
  }
}

function Cargando({ texto }: { texto: string }) {
  return (
    <div className="flex items-center justify-center gap-3 text-sm text-slate-700">
      <Spinner /> {texto}
    </div>
  );
}

/** Página completa: encabezado, contenido y pie (especificación §2.2). */
function Marco({ encabezado, children }: { encabezado?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex h-screen flex-col bg-slate-50">
      {encabezado}
      {children}
      <PiePagina />
    </div>
  );
}

function Centrado({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 text-center shadow-sm">
        {children}
      </div>
    </div>
  );
}

function almacenamiento(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}
